/**
 * Stage Loader Utility
 * Handles loading and parsing of stage JSON configurations
 */

import { StageConfig } from '@/types/StageConfig';

export class StageLoader {
  /**
   * Load a stage configuration from JSON
   * @param stageNumber The stage number to load
   * @returns Promise with the stage configuration
   */
  static async loadStage(stageNumber: number): Promise<StageConfig> {
    try {
      // Load from public folder (Vite serves files from public/)
      const response = await fetch(`/data/stages/stage${stageNumber}.json`);
      
      if (!response.ok) {
        throw new Error(`Failed to load stage ${stageNumber}: ${response.statusText}`);
      }
      
      const stageConfig: StageConfig = await response.json();
      
      // Validate the configuration
      this.validateStageConfig(stageConfig, stageNumber);
      
      console.log(`📋 Stage ${stageNumber} loaded:`, stageConfig.stageName);
      return stageConfig;
      
    } catch (error) {
      console.error(`❌ Error loading stage ${stageNumber}:`, error);
      // Return a default stage configuration as fallback
      return this.getDefaultStage(stageNumber);
    }
  }

  /**
   * Validate stage configuration
   */
  private static validateStageConfig(config: StageConfig, expectedStageNumber: number): void {
    if (config.stageNumber !== expectedStageNumber) {
      console.warn(`⚠️ Stage number mismatch: expected ${expectedStageNumber}, got ${config.stageNumber}`);
    }

    if (!config.balls || config.balls.length === 0) {
      throw new Error('Stage must have at least one ball');
    }

    if (config.timeLimit <= 0) {
      throw new Error('Time limit must be greater than 0');
    }

    console.log(`✅ Stage ${expectedStageNumber} configuration valid`);
  }

  /**
   * Get a default stage configuration (fallback)
   */
  private static getDefaultStage(stageNumber: number): StageConfig {
    console.warn(`⚠️ Using default configuration for stage ${stageNumber}`);
    
    return {
      stageNumber,
      stageName: `Stage ${stageNumber}`,
      timeLimit: 120,
      difficulty: 'easy',
      background: {
        type: 'gradient',
        topColor: '#87CEEB',
        bottomColor: '#1E90FF'
      },
      balls: [
        {
          id: 'default_ball_1',
          size: 'LARGE',
          position: { x: 960, y: 300 },
          velocity: { x: 200, y: -150 }
        }
      ],
      physics: {
        gravity: 980,
        groundY: 1000,
        ballBounceReduction: 0.95
      },
      scoring: {
        timeBonus: 10,
        noHitBonus: 500,
        perfectClearBonus: 1000
      }
    };
  }

  /**
   * Preload stage configurations for caching
   */
  static async preloadStages(stageNumbers: number[]): Promise<Map<number, StageConfig>> {
    const stageMap = new Map<number, StageConfig>();
    
    for (const stageNum of stageNumbers) {
      const config = await this.loadStage(stageNum);
      stageMap.set(stageNum, config);
    }
    
    console.log(`📦 Preloaded ${stageMap.size} stage(s)`);
    return stageMap;
  }
}
